import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row bg-slate-900">
      {/* Left brand panel */}
      <div className="relative lg:w-[46%] bg-slate-900 text-white overflow-hidden flex flex-col justify-between p-10 lg:p-14">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)", backgroundSize: "22px 22px" }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#D9531E] grid place-items-center font-heading font-extrabold text-lg">ICS</div>
          <div className="leading-tight">
            <div className="font-heading font-bold text-lg">Associazione I.C.S.</div>
            <div className="text-xs text-slate-400 uppercase tracking-widest">Gestionale Attestati</div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="inline-flex items-center gap-2 text-[#F6A26B] text-xs font-semibold uppercase tracking-widest mb-4">
            <ShieldCheck className="h-4 w-4" /> Ente accreditato Regione Sicilia
          </div>
          <h1 className="font-heading text-3xl lg:text-4xl font-extrabold leading-tight">
            La piattaforma E-learning di Associazione I.C.S.
          </h1>
        </div>
        <div className="relative z-10 text-xs text-slate-500">CIR: CTW486 · Codice IDu: AG_223</div>
      </div>

      {/* Right form */}
      <div className="flex-1 bg-slate-50 flex items-center justify-center p-6">
        <motion.form
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          onSubmit={submit} className="w-full max-w-sm" data-testid="login-form"
        >
          <h2 className="font-heading text-2xl font-bold text-slate-900">Accedi al gestionale</h2>
          <p className="text-sm text-slate-500 mt-1 mb-8">Inserisci le tue credenziali operatore.</p>

          {error && (
            <div data-testid="login-error" className="mb-5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="nome@academyics.com"
                className="mt-1.5 h-11" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                className="mt-1.5 h-11" required />
            </div>
          </div>

          <Button type="submit" data-testid="login-submit-button" disabled={loading}
            className="w-full mt-7 h-11 text-base font-semibold rounded-xl">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Entra"}
          </Button>
        </motion.form>
      </div>
    </div>
  );
}
